/**
 * Target-price card domain, frontend mirror of
 * backend/src/services/chat_agent/target_price_card.py.
 *
 * The card carries the USER's draft valuation drivers per scenario plus the
 * three probabilities; computed prices live OUTSIDE the card (the compute/save
 * endpoints derive them), so every number here is a draft input, not advice —
 * the "not advice" framing is why the backend's gateway ground-truth line
 * (target_price_card.card_context_line) reads the way it does. Tolerant
 * coercion (clamp + drop, never throw) and the
 * replay rule are kept semantics-identical to the backend so both sides project
 * the same card from the same transcript (transcript-as-state, spec §1).
 *
 * On top of the thesisCard clone this adds the engagement gate
 * ({@link computeEnabled}, spec §11 Q18=B — a mirror of the backend compute
 * fold's scenario-inclusion + probability-mass rules, see
 * {@link isScenarioComputable}) and the save-endpoint provenance mapper
 * ({@link provenanceOf}) — the thesis file has no analogue for either.
 * Pure domain service: no theme, no i18n, no network.
 */
import type { ChatMessage } from './chatApi';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export type ScenarioKey = 'bull' | 'base' | 'bear';

/** One scenario's five draft drivers — the mirror of backend ScenarioDrivers.
 * A bad value None's its field (it does not kill the scenario); only a
 * non-dict snapshot drops the whole scenario. */
export interface ScenarioDrivers {
  revenue_growth: number | null; // CAGR
  ebit_margin: number | null;
  wacc: number | null;
  terminal_growth: number | null;
  base_revenue: number | null;
}

/** Mirror of the backend TargetPriceCard pydantic model. Field order matches
 * the backend declaration order so the coerced object serializes the same
 * shape both sides. */
export interface TargetPriceCard {
  symbol: string;
  bull: ScenarioDrivers | null;
  base: ScenarioDrivers | null;
  bear: ScenarioDrivers | null;
  bull_prob: number | null;
  base_prob: number | null;
  bear_prob: number | null;
  tax_rate: number | null;
  revenue_unit: number | null;
  net_cash: number | null;
  shares_outstanding: number | null;
  currency: string | null;
  horizon_years: number | null;
  /** 2026-10-17 (issue 1): {bull, base, bear} → 1-3 sentence suggestion
   * text. Prose only — never numbers of the build. */
  rationale?: Partial<Record<'bull' | 'base' | 'bear', string>> | null;
}

/** The response shape of POST .../target-price-compute
 * ({ prices, weighted, sensitivity, currency }) — the compute result the sheet
 * holds between "Compute" and "Save"; {@link saveEnabled} consumes it. */
export interface TargetPriceComputed {
  prices: Partial<Record<ScenarioKey, number>>;
  weighted: number | null;
  // Cells are `number | null`: the backend compute endpoint returns null for
  // singular/unresolvable scenario cells (e.g. wacc <= terminal_growth).
  sensitivity?: Record<string, Record<string, number | null>> | null;
  /** The ONE currency every figure above is in — server-derived from the
   * reporting currency of the fundamentals, so it can differ from the exchange
   * currency the position's quote is in (0100.HK reports USD, trades HKD).
   * Absent on pre-currency responses and artifacts. */
  currency?: string | null;
}

/* ------------------------------------------------------------------ */
/*  Constants (backend-parity)                                         */
/* ------------------------------------------------------------------ */

export const PRIMARY_DRIVERS = [
  'revenue_growth',
  'ebit_margin',
  'wacc',
  'terminal_growth',
  'base_revenue',
  'bull_prob',
  'base_prob',
  'bear_prob',
] as const;
export type PrimaryDriver = (typeof PRIMARY_DRIVERS)[number];

/** The five per-scenario drivers — confirmation keyed "<scenario>.<driver>".
 * Exported (with SCENARIO_KEYS/PROB_KEYS/PRIMARY_CONFIRM_KEYS) as the canonical
 * confirmation-key vocabulary: the sheet and its tests derive their row keys
 * from here instead of re-spelling the literals, so a rename here can never
 * silently drift a UI key out of the compute gate's checks. */
export const SCENARIO_KEYS = ['bull', 'base', 'bear'] as const;
export const SCENARIO_DRIVERS = [
  'revenue_growth',
  'ebit_margin',
  'wacc',
  'terminal_growth',
  'base_revenue',
] as const;
/** The three probabilities — confirmation keyed bare. */
export const PROB_KEYS = ['bull_prob', 'base_prob', 'bear_prob'] as const;

/** Scenario → its probability field: the fold weights each computable
 * scenario by its own prob, so the gate sums by this mapping. */
const PROB_KEY_BY_SCENARIO: Record<ScenarioKey, (typeof PROB_KEYS)[number]> = {
  bull: 'bull_prob',
  base: 'base_prob',
  bear: 'bear_prob',
};

/** Every confirmation/provenance key the gate accepts: "<scenario>.<driver>"
 * for each scenario × driver pair, plus the bare probability keys — exactly
 * the lookup shape computeEnabled/provenanceOf use. */
export type ConfirmKey =
  | `${(typeof SCENARIO_KEYS)[number]}.${(typeof SCENARIO_DRIVERS)[number]}`
  | (typeof PROB_KEYS)[number];

/** The full primary-driver confirmation-key list across ALL scenarios (the
 * gate itself only requires the COMPUTABLE scenarios' driver keys, plus the
 * three bare probability keys). */
export const PRIMARY_CONFIRM_KEYS: readonly ConfirmKey[] = [
  ...SCENARIO_KEYS.flatMap((s) => SCENARIO_DRIVERS.map((d): ConfirmKey => `${s}.${d}`)),
  ...PROB_KEYS,
];

// Clamp bounds — exact mirror of the backend Field/`_clamp_num` ranges
// (target_price_card.py ScenarioDrivers/TargetPriceCard). Both sides must
// clamp identically or a card round-trips differently than it projected.
const BOUNDS = {
  revenue_growth: { lo: -0.5, hi: 3.0 },
  ebit_margin: { lo: -1.0, hi: 0.95 },
  wacc: { lo: 0.005, hi: 0.6 },
  terminal_growth: { lo: 0.0, hi: 0.1 },
  base_revenue: { lo: 0.0 }, // ge=0: clamps, unlike the gt=0 fields
  prob: { lo: 0.0, hi: 1.0 }, // bull/base/bear_prob and tax_rate
} as const;

// The compute gate's probability-sum tolerance — mirrors the engine's
// (valuation_kernel.weighted_target) client-side so a gated-on UI never
// meets a backend 422 the other way.
const PROB_SUM_TOLERANCE = 0.05;

/* ------------------------------------------------------------------ */
/*  Coercion helpers (mirrors _clamp_int/_clean_float/_clamp_num)      */
/* ------------------------------------------------------------------ */

function isDict(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

// Backend _clean_float: bool is excluded explicitly in Python (bool ⊂ int);
// in JS `typeof true !== 'number'` covers it. NaN/±Infinity are invalid JSON
// downstream → None/null.
function cleanFloat(raw: unknown): number | null {
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
}

// Backend _clamp_num: bound like thesis's _clamp_int — clamp to the Field
// bounds instead of rejecting; garbage/non-finite becomes null.
function clampNum(raw: unknown, lo?: number, hi?: number): number | null {
  let f = cleanFloat(raw);
  if (f === null) return null;
  if (lo !== undefined && f < lo) f = lo;
  if (hi !== undefined && f > hi) f = hi;
  return f;
}

// Backend _clean_gt0: gt=0 fields have no clampable lower boundary (0 itself
// is invalid), so anything <= 0 is dropped, not clamped.
function cleanGt0(raw: unknown): number | null {
  const f = cleanFloat(raw);
  return f !== null && f > 0 ? f : null;
}

// Backend _clamp_int: trunc toward zero, clamp to [lo, hi]; NaN/±inf garbage → null.
function clampInt(raw: unknown, lo: number, hi: number): number | null {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
  return Math.max(lo, Math.min(hi, Math.trunc(raw)));
}

// Backend _clean_scenario: a bad value nulls its field, it does NOT kill the
// scenario; only a non-dict drops the whole scenario.
function cleanScenario(raw: unknown): ScenarioDrivers | null {
  if (!isDict(raw)) return null;
  return {
    revenue_growth: clampNum(
      raw.revenue_growth,
      BOUNDS.revenue_growth.lo,
      BOUNDS.revenue_growth.hi
    ),
    ebit_margin: clampNum(raw.ebit_margin, BOUNDS.ebit_margin.lo, BOUNDS.ebit_margin.hi),
    wacc: clampNum(raw.wacc, BOUNDS.wacc.lo, BOUNDS.wacc.hi),
    terminal_growth: clampNum(
      raw.terminal_growth,
      BOUNDS.terminal_growth.lo,
      BOUNDS.terminal_growth.hi
    ),
    base_revenue: clampNum(raw.base_revenue, BOUNDS.base_revenue.lo),
  };
}

/** The card symbol's canonical form — the exact backend normalization
 * (strip, upper, slice 16) `coerce_card` applies. Exported so UI-side symbol
 * comparisons (e.g. the builder entries' "same draft → resume" check) compare
 * normalized-to-normalized instead of raw-to-normalized, which silently missed
 * lowercase position symbols like '0700.hk' (review #6). NOT the
 * utils/validation `normalizeSymbol` (that one strips exchange suffixes and
 * leading zeros for portfolio matching) — the card rule keeps them. */
export function normalizeCardSymbol(raw: string): string {
  return raw.trim().toUpperCase().slice(0, 16);
}

/** Tolerant coercion of an agent/agent+user card payload (spec §6 relaxed:
 * clamp + drop, never throw). Returns null when there is no usable card —
 * symbol is the one field backend coercion guarantees, so it gates the whole
 * card. Unknown keys are dropped (the returned object carries exactly the
 * model's fields). Mirrors backend `coerce_card` field-for-field. */
function cleanRationale(raw: unknown): Partial<Record<'bull' | 'base' | 'bear', string>> | null {
  if (!isDict(raw)) return null;
  const out: Partial<Record<'bull' | 'base' | 'bear', string>> = {};
  for (const k of ['bull', 'base', 'bear'] as const) {
    const v = raw[k];
    if (typeof v === 'string' && v.trim()) out[k] = v.slice(0, 500);
  }
  return Object.keys(out).length ? out : null;
}

export function coerceCard(raw: unknown): TargetPriceCard | null {
  if (!isDict(raw)) return null;
  const symbol = raw.symbol;
  if (typeof symbol !== 'string' || !symbol.trim()) return null;
  const currency = raw.currency;
  const currencyValue =
    typeof currency === 'string' && currency.trim()
      ? currency.trim().toUpperCase().slice(0, 8)
      : null;
  return {
    symbol: normalizeCardSymbol(symbol),
    bull: cleanScenario(raw.bull),
    base: cleanScenario(raw.base),
    bear: cleanScenario(raw.bear),
    bull_prob: clampNum(raw.bull_prob, BOUNDS.prob.lo, BOUNDS.prob.hi),
    base_prob: clampNum(raw.base_prob, BOUNDS.prob.lo, BOUNDS.prob.hi),
    bear_prob: clampNum(raw.bear_prob, BOUNDS.prob.lo, BOUNDS.prob.hi),
    tax_rate: clampNum(raw.tax_rate, BOUNDS.prob.lo, BOUNDS.prob.hi),
    revenue_unit: cleanGt0(raw.revenue_unit),
    net_cash: cleanFloat(raw.net_cash),
    shares_outstanding: cleanGt0(raw.shares_outstanding),
    currency: currencyValue,
    horizon_years: clampInt(raw.horizon_years, 1, 15),
    rationale: cleanRationale(raw.rationale),
  };
}

/** Patch a card with user sheet edits: coerce the MERGED dict so every edit
 * passes the same tolerance rules as a full snapshot. Scenario patches merge
 * PER DRIVER — a key present in the patch overwrites (an explicit null clears
 * just that driver), an absent key keeps the card's value, and a non-dict
 * patch value still replaces the scenario outright. Mirror of backend
 * `apply_edit`, which `replayCard` must match row-for-row or a reloaded
 * session folds a different card than the live one. Exported for Task 12's
 * optimistic edit; thesisCard keeps its twin private but the target-price
 * sheet drafts through it. */
export function applyEdit(card: TargetPriceCard, fields: unknown): TargetPriceCard {
  if (!isDict(fields) || Object.keys(fields).length === 0) return card;
  const merged: Record<string, unknown> = { ...card };
  for (const [key, incoming] of Object.entries(fields)) {
    const prior = merged[key];
    merged[key] =
      SCENARIO_KEYS.includes(key as (typeof SCENARIO_KEYS)[number]) &&
      isDict(prior) &&
      isDict(incoming)
        ? { ...prior, ...incoming }
        : incoming;
  }
  merged.symbol = card.symbol; // never editable
  return coerceCard(merged) ?? card;
}

/** Bare (non-scenario) patch keys that ARE wizard stage keys: the three
 * probabilities plus tax_rate — the sheet's advanced block patches tax_rate
 * directly. The other advanced fields (revenue_unit, net_cash, …) are not
 * stages, so they are never accepted. */
const BARE_ACCEPT_KEYS = [...PROB_KEYS, 'tax_rate'] as const;

/** The stage keys an edit POST covers (tp-wizard PR-B task 8): for every
 * scenario dict in `fields`, "<scenario>.<driver>" for each driver present
 * with a non-null value — the sheet's scenario edit carries the WHOLE scenario
 * dict, so presence alone would bulk-stamp every sibling — plus the bare
 * probability keys and tax_rate when patched directly.
 *
 * PR-review fix B (2026-10-25): the list is narrowed to the keys the edit
 * actually CHANGED — the value differs from `card`'s current value for that
 * key (scenario-dict patch keys compare against the card's scenario drivers,
 * bare keys against the card's own attrs) — AND is non-null. The sheet edit
 * must not silently bulk-stamp: a wholesale dict patch carries every sibling
 * driver, and stamping them all confirmed values the user never touched (the
 * wizard would race ahead of the user). Accept-unchanged evidence (spec §1)
 * stays the BUBBLE's job — its chips / accept-all POST the card's own values
 * deliberately and do NOT route through this function.
 *
 * NULL-valued keys are NOT accepts (final-review I2): a stamp is the user's
 * "this value" evidence, so a cleared row / null hole never rides the accept
 * list — the same rule the backend's stamp filter and the bubble's
 * chip-disable enforce, and the edit-key fold of confirmed_driver_keys
 * (confirms only non-None values). The backend filters to its stage
 * vocabulary, so anything else here would be inert anyway. */
export function acceptKeysForEdit(
  fields: Partial<TargetPriceCard>,
  card: TargetPriceCard
): string[] {
  const keys: string[] = [];
  for (const sc of SCENARIO_KEYS) {
    const patch = fields[sc];
    if (patch && typeof patch === 'object') {
      for (const d of SCENARIO_DRIVERS) {
        const v = patch[d];
        if (v != null && v !== card[sc]?.[d]) keys.push(`${sc}.${d}`);
      }
    }
  }
  for (const p of BARE_ACCEPT_KEYS) {
    const v = fields[p];
    if (v != null && v !== card[p]) keys.push(p);
  }
  return keys;
}

/** The typed subset of a sheet edit's keys — the §7 provenance protocol's
 * `edited` wire list (spec 2026-10-27 §7, task 8). A sheet commit IS the
 * user's own typing (T7 review minor): every key {@link acceptKeysForEdit}
 * stamps changed under the user's hand, so the edited list equals the accept
 * list for a sheet row edit. It stays a SEPARATE export so the two wire
 * lists can evolve apart (accept may grow accept-unchanged evidence; edited
 * must stay typed-only) and a caller can never conflate them. The sheet's
 * explicit accept-all button never routes here — the confirm act itself
 * edits nothing (its POST sends edited=[]); `provenanceOf` remains the
 * display-only fallback. */
export function editedKeysForEdit(
  fields: Partial<TargetPriceCard>,
  card: TargetPriceCard
): string[] {
  return acceptKeysForEdit(fields, card);
}

/* ------------------------------------------------------------------ */
/*  Wizard staging (tp-wizard PR-B task 10)                            */
/* ------------------------------------------------------------------ */

/** The tp-wizard stage list — exact mirror of the backend STAGE_ORDER
 * (target_price_card.py, task 2 = the binding contract): probabilities lead
 * (the owner's flow judges the distribution first), then one stage per
 * numeric driver; each stage confirms three keys ({@link stageKeysFor}).
 * Card-level facts (revenue_unit, net_cash, shares, currency) and the
 * base_revenue anchor are BASIS-seeded server-side and never user-staged, so
 * they have NO stage — which is why the fold vocabulary below is NOT
 * {@link PRIMARY_CONFIRM_KEYS} (that 18-key list includes the base_revenue
 * anchors). Stage keys derive from the shared SCENARIO_KEYS/PROB_KEYS vocab,
 * so the two sides spell the dotted keys identically. */
export const WIZARD_STAGE_ORDER = [
  'probabilities',
  'revenue_growth',
  'ebit_margin',
  'wacc',
  'terminal_growth',
] as const;

/** The per-stage confirmation keys: "probabilities" → the three bare prob
 * keys; a driver stage → "<scenario>.<driver>" for each of bull/base/bear
 * (SCENARIO_KEYS order). Mirrors the backend STAGE_KEYS[stage]. */
export function stageKeysFor(stage: string): string[] {
  if (stage === 'probabilities') return [...PROB_KEYS];
  return SCENARIO_KEYS.map((sc) => `${sc}.${stage}`);
}

/** The full wizard confirmation vocabulary — the client mirror of the
 * backend ALL_STAGE_KEYS (3 probs + 3 scenarios × the 4 staged drivers = 15
 * keys). An `accept` chip's key list is filtered through THIS set before it
 * can arm anything ({@link cleanAccept} in components/chat/question), so a key
 * outside it can never be confirmed client-side. */
export const ALL_STAGE_KEYS: readonly string[] = WIZARD_STAGE_ORDER.flatMap((s) =>
  stageKeysFor(s)
);

/** The wizard's current stage: the first stage ({@link WIZARD_STAGE_ORDER}
 * canonical order) with ANY key missing from `confirmed`; null when every
 * key is confirmed (the build is complete). Client mirror of the backend
 * `current_stage`; Task 12's computeEnabled extension consumes it. */
export function currentStageClient(confirmed: Set<string>): string | null {
  for (const stage of WIZARD_STAGE_ORDER) {
    if (stageKeysFor(stage).some((k) => !confirmed.has(k))) return stage;
  }
  return null;
}

/** The card's number at a confirmation key ("<scenario>.<driver>" or a bare
 * probability), null when absent — the shared value-at lookup. The sheet's
 * confirm-all filter (PR-review fix B: remaining keys must carry a value) and
 * the screen's bubble-accept handlers read the live card through THIS, so the
 * two surfaces cannot grow divergent key→value resolutions. */
export function cardValueAt(card: TargetPriceCard, key: string): number | null {
  const dict = card as unknown as Record<string, unknown>;
  const dot = key.indexOf('.');
  if (dot === -1) {
    return (dict[key] as number | null | undefined) ?? null;
  }
  const sc = dict[key.slice(0, dot)] as Record<string, unknown> | null | undefined;
  return (sc?.[key.slice(dot + 1)] as number | null | undefined) ?? null;
}

/* ------------------------------------------------------------------ */
/*  Replay (transcript-as-state)                                       */
/* ------------------------------------------------------------------ */

/** JSONB-safe canonical form of a card fingerprint: recurse, strip nulls (the
 * backend stores `model_dump(exclude_none=True)` — nulls never reach the wire
 * here, and coerceCard's absent keys stringify as undefined anyway), sort
 * keys (JSONB does not preserve key order), compare by string. */
function canonicalFingerprint(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(canonicalFingerprint);
  if (!isDict(v)) return v;
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(v).sort()) {
    const val = v[k];
    if (val === null || val === undefined) continue;
    out[k] = canonicalFingerprint(val);
  }
  return out;
}

/** Whether a coerced card matches an artifact's `card_fingerprint` — the
 * client mirror of the narration gate's like-for-like compare (both sides are
 * the exclude_none dump form; the M2 saved arm only rehydrates the panel when
 * the receipt's card IS the computed one). */
function fingerprintMatches(card: TargetPriceCard, fingerprint: unknown): boolean {
  if (!isDict(fingerprint)) return false;
  return (
    JSON.stringify(canonicalFingerprint(card)) ===
    JSON.stringify(canonicalFingerprint(fingerprint))
  );
}

/** The artifact → sheet-shape mapping shared by replayComputed's live and
 * saved arms. Old rows predating `sensitivity` degrade to prices/weighted
 * (the sheet renders the grid only when the field is carried), and ones
 * predating `currency` carry no label — the sheet then falls back to the card's
 * own, which is why the fold's derived label wins whenever it is present;
 * `upside_pct`/`price_at_compute`/`card_fingerprint` are the narration layer's
 * fields and stay out of the sheet's shape. */
function computedViewOf(artifact: Record<string, unknown>): TargetPriceComputed {
  const currency = typeof artifact.currency === 'string' ? artifact.currency.trim() : '';
  return {
    prices: artifact.prices as TargetPriceComputed['prices'],
    weighted: typeof artifact.weighted === 'number' ? artifact.weighted : null,
    sensitivity: isDict(artifact.sensitivity)
      ? (artifact.sensitivity as TargetPriceComputed['sensitivity'])
      : undefined,
    ...(currency ? { currency } : {}),
  };
}

/** Replay over `ChatMessage[]` in ChatConversation order (oldest first), the
 * frontend entry shape Task 12 consumes; semantics mirror backend
 * `replay_card`, extended with the saved arm (PR-review M2): the scan runs
 * backwards, the latest `target_price_card` snapshot wins, `target_price_edit`
 * patches newer than it fold in oldest→newest, and a `target_price_saved`
 * receipt newer than the winning snapshot ENDS the draft — a card-LESS
 * receipt (pre-arm legacy) returns null, so a finished build is never
 * re-projected as in-progress. A receipt CARRYING a `card` payload (the save
 * route persists the exact card form) resolves that card instead — mirroring
 * backend `narration_card_state`'s saved arm — unless a
 * `target_price_dismissed` row stands above it (an explicit ✕ kills the
 * state). An edit alone does not resume a receipt-ended build; a NEW snapshot
 * after the receipt does. The live arm still ignores the dismissal marker
 * (the UI gate owns visibility there), as before. */
export function replayCard(messages: ChatMessage[]): TargetPriceCard | null {
  const edits: unknown[] = [];
  let dismissedNewer = false; // dismissal seen above the row being read (saved-arm veto)
  for (let i = messages.length - 1; i >= 0; i--) {
    const meta = messages[i]?.metadata;
    if (!isDict(meta)) continue;
    if (meta.target_price_dismissed != null) dismissedNewer = true;
    const edit = meta.target_price_edit; // checked first: a meta with both is an edit
    if (isDict(edit)) {
      edits.push(edit);
      continue;
    }
    if (meta.target_price_saved != null) {
      // M2 saved arm: the receipt's own card is the state — a saved session
      // reopens the sheet with what was saved. No newer dismissal may stand
      // above it; an edit above it does NOT fold in (mirror of the server:
      // an edit alone does not resume).
      const saved = meta.target_price_saved;
      const savedCard = isDict(saved) ? coerceCard(saved.card) : null;
      if (savedCard !== null && !dismissedNewer) return savedCard;
      return null; // legacy card-less receipt → no active draft (unchanged)
    }
    const card = coerceCard(meta.target_price_card);
    if (card !== null) {
      let folded = card;
      for (let j = edits.length - 1; j >= 0; j--) {
        folded = applyEdit(folded, edits[j]); // oldest→newest
      }
      return folded;
    }
  }
  return null;
}

/** Whether the transcript's latest draft-state row is a CARD-CARRYING
 * `target_price_saved` receipt with no dismissal standing above it —
 * precisely the condition under which {@link replayCard}'s saved arm returns
 * the receipt's card. Mirrors that arm row-for-row (backwards scan; edits are
 * transparent — an edit alone never resumes a receipt-ended build; a NEWER
 * card snapshot re-opened the draft, so this is a live session again; a
 * dismissal above the receipt vetoes). Kept a separate function rather than
 * refactoring replayCard so the well-tested replay path stays untouched; the
 * mirror contract is pinned by the ChatConversation saved-reload probe.
 *
 * This is what the screen derives `targetPriceSaved` from on load (fix-wave
 * 3): the flag used to be set only by the live save handler, so a reloaded
 * saved session rendered the EDITABLE draft sheet — edits 422ed and rolled
 * back with the raw server string, and a further Save press appended a
 * duplicate receipt. */
export function savedReceiptHolds(messages: ChatMessage[]): boolean {
  let dismissedNewer = false;
  for (let i = messages.length - 1; i >= 0; i--) {
    const meta = messages[i]?.metadata;
    if (!isDict(meta)) continue;
    if (meta.target_price_dismissed != null) dismissedNewer = true;
    if (isDict(meta.target_price_edit)) continue;
    if (meta.target_price_saved != null) {
      const saved = meta.target_price_saved;
      return !dismissedNewer && isDict(saved) && coerceCard(saved.card) !== null;
    }
    if (coerceCard(meta.target_price_card) !== null) return false; // newer snapshot → live draft
  }
  return false;
}

/** Replay the sheet's computed view from the transcript (spec §3 panel
 * projection, 2026-10-27): the newest `target_price_computed` artifact
 * (persisted by the compute route as a system meta row) rehydrates
 * `TargetPriceComputed` on load, so the output panel survives a reload —
 * `replayCard`'s twin for the derived numbers. The scan walks backwards and
 * the FIRST draft-state row decides, mirroring the live path's "a draft
 * change invalidates it" rule: a `target_price_card` snapshot, a
 * `target_price_edit`, or a `target_price_saved` receipt newer than the
 * artifact → null (numbers from an older card are stale / the build ended);
 * the artifact itself → its outputs. The saved arm (PR-review M2): a receipt
 * carrying a card whose fingerprint matches the artifact BELOW it rehydrates
 * the panel on a saved session — the client mirror of the narration gate's
 * saved-arm rule (no newer snapshot/dismissal; a mismatched or missing
 * fingerprint fails closed). `target_price_dismissed` vetoes only the saved
 * arm (the live arm leaves visibility to the UI gate, as before). */
export function replayComputed(messages: ChatMessage[]): TargetPriceComputed | null {
  let dismissedNewer = false;
  for (let i = messages.length - 1; i >= 0; i--) {
    const meta = messages[i]?.metadata;
    if (!isDict(meta)) continue;
    if (meta.target_price_dismissed != null) dismissedNewer = true;
    if (meta.target_price_saved != null) {
      // M2 saved arm: the receipt's card, fingerprint-matched against the
      // newest artifact below it, is what the panel shows on a saved session.
      const saved = meta.target_price_saved;
      const savedCard = isDict(saved) ? coerceCard(saved.card) : null;
      if (savedCard === null || dismissedNewer) return null;
      for (let j = i - 1; j >= 0; j--) {
        const olderMeta = messages[j]?.metadata;
        if (!isDict(olderMeta)) continue;
        const artifact = olderMeta.target_price_computed;
        if (isDict(artifact) && isDict(artifact.prices)) {
          if (!fingerprintMatches(savedCard, artifact.card_fingerprint)) return null;
          return computedViewOf(artifact);
        }
      }
      return null;
    }
    if (isDict(meta.target_price_edit)) return null; // draft moved after the compute
    if (coerceCard(meta.target_price_card) !== null) return null; // newer snapshot → stale
    const artifact = meta.target_price_computed;
    if (isDict(artifact) && isDict(artifact.prices)) {
      return computedViewOf(artifact);
    }
  }
  return null;
}

/** Prune the engagement-confirmation map against an AGENT-originated card
 * replacement (final-review FIX 3): a `<scenario>.<driver>` (or bare
 * probability) confirmation means "the user confirmed THIS value". When a
 * re-emitted card CHANGES the value behind a confirmed key, that confirmation
 * is stale — the compute gate must not stay armed for a value the user never
 * confirmed, and provenance must not mislabel it 'user'. Keys whose value is
 * unchanged keep their confirmation; a scenario added or removed by the
 * re-emission prunes that scenario's keys (present one side, absent the other
 * IS a value change). Returns the original reference when nothing changed, so
 * an identical echo never re-renders. ONLY agent replacement prunes: the
 * edit-POST response reflects the user's own edit, and that path keeps the
 * map untouched. */
export function pruneConfirmedForReplacement(
  oldCard: TargetPriceCard | null,
  newCard: TargetPriceCard,
  confirmed: Record<string, boolean>
): Record<string, boolean> {
  // null/undefined normalize to one "absent" value so an absent-on-both-sides
  // key counts as unchanged (backend exclude_none framing).
  const valueAt = (card: TargetPriceCard | null, key: string): unknown => {
    if (card == null) return null;
    const dot = key.indexOf('.');
    if (dot === -1) return (card as Record<string, unknown>)[key] ?? null;
    const scenario = (card as Record<string, unknown>)[key.slice(0, dot)];
    if (!isDict(scenario)) return null;
    return scenario[key.slice(dot + 1)] ?? null;
  };
  let pruned = false;
  const out: Record<string, boolean> = {};
  for (const [key, v] of Object.entries(confirmed)) {
    // Falsy entries are not confirmations — pass them through untouched.
    if (!v || valueAt(oldCard, key) === valueAt(newCard, key)) out[key] = v;
    else pruned = true;
  }
  return pruned ? out : confirmed;
}

/* ------------------------------------------------------------------ */
/*  Engagement gate + progress + save                                  */
/* ------------------------------------------------------------------ */

/** Server mirror of the compute fold's scenario-inclusion test
 * (backend/src/api/routes/chat.py `_target_price_computed`): a scenario joins
 * the price set ONLY when its dict exists AND all five drivers are non-null —
 * a partial draft is not a number. The fold then weights THAT subset's
 * probabilities, so an incomplete scenario contributes no probability mass
 * either. The engagement gate below must use this predicate (not bare
 * `card[k] != null`) or it arms on cards the backend 422s. */
export function isScenarioComputable(drivers: ScenarioDrivers | null | undefined): boolean {
  if (drivers == null) return false;
  return SCENARIO_DRIVERS.every((d) => drivers[d] != null);
}

/** The compute gate (spec §11 Q18=B), a deliberate MIRROR of the server-side
 * compute fold (`_target_price_computed`): enabled only when
 *  1. at least one scenario is COMPUTABLE ({@link isScenarioComputable} —
 *     dict present AND all five drivers non-null, the fold's own inclusion
 *     test), and
 *  2. every one of THOSE scenarios' five drivers has been actively
 *     confirmed/edited by the user, and
 *  3. all three probability keys are confirmed (they are the compliance
 *     surface the builder shows, so they stay required even where a scenario
 *     is absent), and
 *  4. the probabilities of the COMPUTABLE scenarios sum to 1 within ±0.05 —
 *     the fold calls weighted_target with `(price, prob ?? 0.0)` for exactly
 *     that subset, so an agent-seeded base-only card carries base_prob's mass
 *     alone and 422s unless it is ~1.0. Summing all three (the old behaviour)
 *     armed a gate the backend always rejected.
 * This is what carries the "tool not advice" weight — value-presence is NOT
 * enough; the `confirmed` map the UI maintains is authoritative. Per-scenario
 * drivers are keyed "<scenario>.<driver>"; the probabilities keyed bare. */
/**
 * 2026-10-17 (owner ruling, issue 2 = option C): the per-driver confirmation
 * gate is REMOVED — compute is armed by VALUE PRESENCE, and the single
 * "確認並計算 / Confirm & Calculate" press is the engagement act. The
 * `confirmed` map remains the provenance input (edited → 'user'), but it no
 * longer gates compute. Values the AI proposed and the user left untouched
 * are accepted by the one button press — the §15 posture moved from
 * per-driver confirmation to one explicit accept-and-compute action.
 *
 * tp-wizard PR-B task 12 (review mode, §1): the OPTIONAL second arg re-arms
 * the engagement requirement for the wizard flow — when the caller supplies
 * the server's confirmed-key set (`target_price_state.confirmed` off the
 * newest transcript page, B3),
 * compute ALSO requires {@link currentStageClient}(confirmedKeys) === null,
 * i.e. every wizard stage confirmed. The set is consumed as-is (never
 * reimplemented here) and ABSENT means the value-presence-only behaviour, so
 * un-migrated callers keep the option-C semantics.
 */
export function computeEnabled(card: TargetPriceCard, confirmedKeys?: Set<string>): boolean {
  const computable = SCENARIO_KEYS.filter((k) => isScenarioComputable(card[k]));
  if (!computable.length) return false;
  // Only the computable scenarios feed the fold's weighted_target call.
  const sum = computable.reduce((acc, s) => acc + (card[PROB_KEY_BY_SCENARIO[s]] ?? 0), 0);
  if (Math.abs(sum - 1) > PROB_SUM_TOLERANCE) return false;
  // Review-mode arming: values the fold accepts are still not enough while a
  // wizard stage is unconfirmed. (A confirmations-complete set can never
  // RESCUE a card the value gate rejects — this check only ever disarms.)
  if (confirmedKeys != null && currentStageClient(confirmedKeys) !== null) return false;
  return true;
}

/** {driver_key: "user" | "ai_accepted"} for the save endpoint's §15 audit
 * trail (backend TargetPriceSaveRequest.provenance).
 * Keys: per-scenario "<scenario>.<driver>" plus bare bull_prob/base_prob/
 * bear_prob. Confirmed → 'user'; present-but-unconfirmed (came from the AI
 * seed) → 'ai_accepted'; absent fields contribute no key. A confirmed key is
 * 'user' even if its value is null.
 * DEVIATION (documented per plan): the plan sketch showed `provenanceOf
 * (confirmed)`; knowing "present-but-unconfirmed" requires the card, so the
 * shipped signature is `provenanceOf(card, confirmed)` — Task 11/12 call it
 * with the projected card and the same confirmed map they pass to
 * computeEnabled. */
export function provenanceOf(
  card: TargetPriceCard,
  confirmed: Record<string, boolean>
): Record<string, 'user' | 'ai_accepted'> {
  const out: Record<string, 'user' | 'ai_accepted'> = {};
  for (const s of SCENARIO_KEYS) {
    const sc = card[s];
    if (sc == null) continue;
    for (const d of SCENARIO_DRIVERS) {
      const key = `${s}.${d}`;
      if (confirmed[key]) out[key] = 'user';
      else if (sc[d] != null) out[key] = 'ai_accepted';
    }
  }
  for (const p of PROB_KEYS) {
    if (confirmed[p]) out[p] = 'user';
    else if (card[p] != null) out[p] = 'ai_accepted';
  }
  return out;
}

/** Draft-completeness for the progress bar: value-based (the engagement
 * doctrine lives in computeEnabled, not here). Total = the five drivers of
 * each ACTIVE scenario + the three probabilities; filled = the non-null ones.
 * No active scenario → the probabilities alone (0..3 of 3).
 * NOTE: "active" here is dict-presence (`card[k] != null`), which is WIDER
 * than the gate's {@link isScenarioComputable} — deliberate: the bar reports
 * how much of the draft is filled in, including a partially-seeded scenario
 * the compute fold would skip. It shares no semantics with the server fold, so
 * it must not be "aligned" to it. */
export function cardProgress(card: TargetPriceCard): { filled: number; total: number } {
  const scenarios = SCENARIO_KEYS.filter((k) => card[k] != null);
  const total = scenarios.length * SCENARIO_DRIVERS.length + PROB_KEYS.length;
  let filled = 0;
  for (const s of scenarios) {
    const sc = card[s];
    if (sc == null) continue; // unreachable; keeps TS happy without !
    for (const d of SCENARIO_DRIVERS) {
      if (sc[d] != null) filled++;
    }
  }
  for (const p of PROB_KEYS) {
    if (card[p] != null) filled++;
  }
  return { filled, total };
}

/** Save arm-check: the builder persists the COMPUTED numbers, so Save is
 * enabled only once a compute round-trip produced a finite weighted target
 * (spec §12 — computed values exist only after the compute endpoint ran).
 * The card-side precondition is {@link computeEnabled}; the sheet gates on
 * both. `null`/`undefined` accepted so callers can pass a not-yet-run result
 * straight through. */
export function saveEnabled(computed: TargetPriceComputed | null | undefined): boolean {
  return (
    computed != null && typeof computed.weighted === 'number' && Number.isFinite(computed.weighted)
  );
}

/**
 * The seq of the LATEST `target_price_card` snapshot in the transcript (the
 * message the winning replay card came from), or null when none exists.
 * The drain-delivered seed turn's card lands ONLY in the transcript (the
 * user's SSE stream already closed), so the open sheet learns about it on
 * transcript refetch — this locator lets the component detect "a snapshot
 * NEWER than the one I applied" and live-update instead of waiting for a
 * reload (2026-10-16: the card pre-fill was refresh-only).
 */
export function latestCardSnapshotSeq(messages: ChatMessage[]): number | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const meta = messages[i]?.metadata;
    if (!isDict(meta)) continue;
    if (isDict(meta.target_price_edit)) continue; // edits are not snapshots
    if (meta.target_price_saved != null) return null; // receipt ends the draft
    if (coerceCard(meta.target_price_card) !== null) {
      return messages[i]?.seq ?? null;
    }
  }
  return null;
}

/**
 * The seq of the LATEST `target_price_dismissed` receipt in the transcript, or
 * null when there is none. The live-update planner keys its dismissal re-arm
 * off THIS (a persisted receipt) rather than the in-memory dismissed flag, so a
 * dismissed card whose snapshot is still in the transcript cannot reappear on
 * the next refetch — see {@link planCardLiveUpdate}.
 */
export function latestDismissalSeq(messages: ChatMessage[]): number | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const meta = messages[i]?.metadata;
    if (isDict(meta) && meta.target_price_dismissed != null) {
      return messages[i]?.seq ?? null;
    }
  }
  return null;
}

/** The decision {@link planCardLiveUpdate} returns to the update effect. */
export type CardLiveUpdate =
  | { kind: 'none' }
  | { kind: 'echo'; seq: number }
  | { kind: 'apply'; seq: number; card: TargetPriceCard; clearDismissed: boolean };

/**
 * PURE planner for the open sheet's transcript-driven live update — the
 * decision the ChatConversation update effect applies, extracted so it is
 * unit-testable without the component harness (its WS mock never captures the
 * refetch handler that drives this path).
 *
 * `state` is the sheet's card-relevant snapshot at effect time:
 * - `appliedSeq` — seq of the last snapshot applied (null if the card arrived
 *   via SSE and has not been pinned to a transcript seq yet);
 * - `prevCard` — the card currently in the sheet (null after a dismissal);
 * - `dismissed` — the in-memory dismissed flag.
 *
 * Rules, in order:
 * 1. no winning snapshot → `none`;
 * 2. a snapshot not NEWER than `appliedSeq` → `none` (already applied);
 * 3. dismissed → re-arm ONLY once the transcript shows a snapshot NEWER than
 *    the dismissal receipt. No receipt yet (an in-session dismiss, or an
 *    SSE-delivered card not yet persisted) or a receipt newer than the snapshot
 *    → `none`. This is what stops a dismissed card reappearing on refetch;
 * 4. an identical re-emission of `prevCard` → `echo` (record the seq, change
 *    nothing — the agent re-emits every builder turn);
 * 5. otherwise → `apply` with the replayed card; `clearDismissed` says the
 *    effect must also reset the in-memory dismissed flag (rule 3 re-arm).
 */
export function planCardLiveUpdate(
  messages: ChatMessage[],
  state: { appliedSeq: number | null; prevCard: TargetPriceCard | null; dismissed: boolean }
): CardLiveUpdate {
  const seq = latestCardSnapshotSeq(messages);
  if (seq == null) return { kind: 'none' };
  if (state.appliedSeq != null && seq <= state.appliedSeq) return { kind: 'none' };

  let clearDismissed = false;
  if (state.dismissed) {
    const dSeq = latestDismissalSeq(messages);
    if (dSeq == null || seq <= dSeq) return { kind: 'none' };
    clearDismissed = true;
  }

  const card = replayCard(messages);
  if (!card) return { kind: 'none' };
  if (state.prevCard && JSON.stringify(state.prevCard) === JSON.stringify(card)) {
    return { kind: 'echo', seq };
  }
  return { kind: 'apply', seq, card, clearDismissed };
}
